import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Search, Star } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const AGES = ["All", "0-3M", "3-6M", "6-9M", "9-12M", "12-18M", "18-24M", "1-2Y", "2-3Y", "3-4Y", "4-5Y"];
const GENDERS = ["All", "Girl", "Boy"];
const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "rating", label: "Top Rated" },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full px-4 py-2 font-mono text-xs tracking-wide transition-all duration-300 border ${
        active
          ? "bg-ink text-cream border-ink"
          : "bg-white/70 text-charcoal border-ink/10 hover:border-terracotta hover:text-terracotta"
      }`}
    >
      {children}
    </button>
  );
}

function ProductCard({ product, index }) {
  const { setSelectedProduct, wishlist, toggleWishlist } = useStore();
  const wished = wishlist.includes(product.id);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.06, 0.4), ease: [0.22, 1, 0.36, 1] }}
      className="group cursor-pointer"
      onClick={() => setSelectedProduct(product)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden rounded-2xl bg-blush">
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        {product.badge && (
          <span className="absolute top-3 left-3 bg-ink text-cream font-mono text-[10px] tracking-widest uppercase rounded-full px-3 py-1.5">
            {product.badge}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
          className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full p-2.5 shadow hover:scale-110 transition-transform duration-300"
          data-testid={`wishlist-btn-${product.id}`}
          aria-label="Toggle wishlist"
        >
          <Heart className={`w-4 h-4 ${wished ? "fill-terracotta text-terracotta" : "text-charcoal"}`} />
        </button>
        <div className="absolute inset-x-3 bottom-3 translate-y-14 group-hover:translate-y-0 transition-transform duration-500 ease-out">
          <span className="block text-center bg-ink/90 backdrop-blur text-cream rounded-full py-3 text-sm font-semibold">
            View & Choose Size
          </span>
        </div>
        {product.stock <= 10 && (
          <span className="absolute bottom-3 left-3 group-hover:opacity-0 transition-opacity bg-peach text-terracotta-dark font-mono text-[10px] rounded-full px-2.5 py-1">
            Only {product.stock} left
          </span>
        )}
      </div>
      <div className="pt-4 px-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-sans font-semibold text-ink text-base sm:text-lg leading-snug">{product.name}</h3>
          {product.rating && (
            <span className="flex items-center gap-1 font-mono text-xs text-charcoal shrink-0 mt-1">
              <Star className="w-3.5 h-3.5 fill-gold text-gold" /> {product.rating}
            </span>
          )}
        </div>
        <p className="font-mono text-[10px] tracking-widest uppercase text-sage-dark mt-1">{product.fabric}</p>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="font-serif font-bold text-xl text-ink">{inr(product.price)}</span>
          {product.mrp > product.price && (
            <span className="text-sm text-charcoal/50 line-through">{inr(product.mrp)}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-2.5">
          {product.sizes.map((s) => (
            <span key={s} className="font-mono text-[10px] border border-ink/10 rounded-full px-2 py-0.5 text-charcoal">{s}</span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [age, setAge] = useState("All");
  const [gender, setGender] = useState("All");
  const [sort, setSort] = useState("featured");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (age !== "All") params.set("age", age);
    if (gender !== "All") params.set("gender", gender);
    if (sort) params.set("sort", sort);
    if (search.trim()) params.set("search", search.trim());
    api.get(`/products?${params.toString()}`)
      .then((r) => setProducts(r.data.products))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [age, gender, sort, search]);

  return (
    <section id="shop" className="py-20 md:py-28 bg-white/50" data-testid="shop-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-sage-dark mb-4">The Collection • AW '26</p>
          <h2 className="font-serif font-black text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight leading-[1.1]">
            Dresses they'll outgrow, <em className="text-terracotta">memories that won't.</em>
          </h2>
        </motion.div>

        <div className="space-y-4 mb-10" data-testid="shop-filters">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-charcoal/60 w-20">For</span>
            {GENDERS.map((g) => (
              <Chip key={g} active={gender === g} onClick={() => setGender(g)} testid={`filter-gender-${g.toLowerCase()}`}>{g}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-charcoal/60 w-20">Age</span>
            {AGES.map((a) => (
              <Chip key={a} active={age === a} onClick={() => setAge(a)} testid={`filter-age-${a.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>{a}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/50" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search mulmul, anarkali, twirl..."
                className="w-full rounded-full border border-ink/10 bg-white/80 pl-11 pr-4 py-2.5 text-sm outline-none focus:border-terracotta transition-colors"
                data-testid="shop-search-input"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-ink/10 bg-white/80 px-4 py-2.5 text-sm font-medium outline-none cursor-pointer"
              data-testid="shop-sort-select"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <span className="font-mono text-xs text-charcoal/60" data-testid="shop-result-count">{products.length} styles</span>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-blush rounded-2xl aspect-[3/4]" />
                <div className="h-4 bg-blush rounded-full mt-4 w-3/4" />
                <div className="h-3 bg-blush rounded-full mt-2 w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24" data-testid="shop-empty-state">
            <p className="font-serif italic text-2xl text-charcoal">No styles match those filters yet.</p>
            <button
              onClick={() => { setAge("All"); setGender("All"); setSearch(""); }}
              className="mt-6 rounded-full bg-terracotta text-white px-6 py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors"
              data-testid="shop-clear-filters-btn"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <AnimatePresence mode="popLayout">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
}
